import {Button} from '@/components/ui/button/Button'
import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useProjectMutationSuccess} from '@/features/projects/hooks/useProjectMutationSuccess'
import {useStoreTeam} from '@/features/treatment-requests/teams/queries/useStoreTeam'
import {useTranslation} from '@/translations/i18n'
import {errorHandler, ObjectKeys} from '@/utilities/helpers'
import {useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {Spinner} from '@/components/ui/spinner/Spinner'

const EditProjectTeamFormFooterSchema = ProjectFieldsSchema.pick({
    id: true,
    treatmentRequestId: true,
    team: true
})
type EditProjectTeamFormFooterSchema = z.infer<typeof EditProjectTeamFormFooterSchema>

export const EditProjectTeamFormFooter = ({
    onCancel,
    isAReserve,
    patientId
}: {
    onCancel: () => void
    isAReserve?: boolean
    patientId: string
}) => {
    const {t} = useTranslation()
    const form = useFormContext<EditProjectTeamFormFooterSchema>()
    const treatmentRequestId = useWatch({control: form.control, name: 'treatmentRequestId'})
    const projectId = useWatch({control: form.control, name: 'id'})

    const handleSuccessProjectMutation = useProjectMutationSuccess({
        projectId,
        onComplete: onCancel
    })

    const {mutate: storeTeam, isPending} = useStoreTeam(treatmentRequestId ? {treatmentRequestId} : {projectId}, {
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, projectId]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TEAM_MEMBERS, {treatmentRequestId}]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT, patientId]})
            await handleSuccessProjectMutation()
        },
        onError: errorHandler
    })

    const handleSubmit = form.handleSubmit(formValues => {
        const referringDoctors = formValues.team.filter(team => team.group == 'referring_doctor').map(team => team.id)
        const therapists = formValues.team.filter(team => team.group == 'therapist').map(team => team.id)
        const almoners = formValues.team.filter(team => team.group == 'almoner').map(team => team.id)
        const psychologists = formValues.team.filter(team => team.group == 'psychologist').map(team => team.id)
        const doctors = formValues.team.filter(team => team.group == 'doctor').map(team => team.id)
        const other = formValues.team.filter(team => team.group == 'other').map(team => team.id)

        storeTeam({
            projectId,
            isAReserve: !!isAReserve,
            teams: [
                {
                    group: 'referring_doctor',
                    users: referringDoctors
                },
                {
                    group: 'therapist',
                    users: therapists
                },
                {
                    group: 'almoner',
                    users: almoners
                },
                {
                    group: 'psychologist',
                    users: psychologists
                },
                {
                    group: 'doctor',
                    users: doctors
                },
                {
                    group: 'other',
                    users: other
                }
            ]
        })
    })

    return (
        <Flexbox fullWidth align="center" justify="end" gap={2}>
            <DialogDismiss onClick={onCancel} render={<Button variant="secondary" />}>
                {t('commons:close')}
            </DialogDismiss>
            <Button
                variant="primaryNeutral"
                onClick={handleSubmit}
                disabled={isPending || ObjectKeys(form.formState.dirtyFields).length == 0}
            >
                {t('commons:save')}
                {isPending && <Spinner />}
            </Button>
        </Flexbox>
    )
}
